import { ReactNode, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PdfView from "components/pdf-view/Pdf_modal";
import Notice from "components/tw/notice";
import { btnIconCls, btnLinkCls, btnPrimaryCls } from "components/tw/classes";
import { daysSince, Translate } from "features/cabinet-tasks/task-row";
import { leaveCalendarLeaveTypeI18nKey } from "features/leave-calendar/leaveCalendar.constants";
import { LeaveFacts } from "services/cabinet-queues.service";
import { withThousands } from "./format-count";
import SheetFooter from "./queue-sheet-footer";
import { QueueWalk, SheetMode, Tally, WalkEntry, WalkView } from "./use-queue-walk";

/**
 * The «iš eilės» sheet: a drawer on a wide screen and a full-screen sheet on a phone. It
 * only draws what `useQueueWalk` says; every decision is made there.
 */
export default function QueueSheet({ walk }: { walk: QueueWalk }) {
  const { t } = useTranslation();
  const { view } = walk;
  if (!view) return null;

  // The header says what kind of walk it is; a document in it adds «i iš N».
  const word = t(`queue_sheet_word_${view.mode}`);
  const title = view.entry
    ? `${word} · ${t("queue_sheet_counter", { i: withThousands(view.position), n: withThousands(view.total) })}`
    : word;
  // While a request runs, the sheet stays open and the document stays where it is, so its answer is seen.
  const isWorking = view.phase.name === "working";

  let content: ReactNode;
  if (view.entry) {
    content = <EntryScreen key={view.entry.item.id} walk={walk} view={view} entry={view.entry} isLocked={isWorking} />;
  } else if (view.isFinished) {
    content = (
      <>
        <div className="flex-1 space-y-3 px-4 py-4">
          <p className="text-base font-semibold text-gray-900">
            {t("queue_end_title", { summary: endSummary(t, view.kind, view.tally) })}
          </p>
          {view.moreRowsFailed && <Notice tone="warning">{t("queue_end_more_failed")}</Notice>}
        </div>
        <footer className="border-t border-gray-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button type="button" className={btnPrimaryCls} onClick={walk.close}>
            {t("close")}
          </button>
        </footer>
      </>
    );
  } else {
    content = <p className="flex-1 px-4 py-4 text-sm text-gray-500">{t("queue_sheet_loading_more")}</p>;
  }

  return (
    <Dialog open onClose={walk.close} className="relative z-[1000]">
      <DialogBackdrop className="fixed inset-0 bg-zinc-950/40" />
      <div className="fixed inset-0 flex justify-end">
        <DialogPanel className="flex h-full w-full flex-col bg-white shadow-xl md:w-[min(880px,100vw)]">
          <header className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
            <DialogTitle className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">{title}</DialogTitle>
            <button
              type="button"
              onClick={walk.close}
              disabled={isWorking}
              aria-label={t("close")}
              className={`${btnIconCls} max-md:size-11`}
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          </header>
          {content}
        </DialogPanel>
      </div>
    </Dialog>
  );
}

// «pasirašyta 2, atmesta 1, praleista 0»; the Dokobit part only when a document was opened there.
function endSummary(t: Translate, kind: WalkEntry["kind"], tally: Tally): string {
  if (kind === "review") {
    return [t("queue_end_reviewed", { n: tally.done }), t("queue_end_skipped", { n: tally.skipped })].join(", ");
  }
  const parts = [
    t("queue_end_signed", { n: tally.done }),
    t("queue_end_declined", { n: tally.declined }),
    t("queue_end_skipped", { n: tally.skipped }),
  ];
  if (tally.opened > 0) parts.push(t("queue_end_opened", { n: tally.opened }));
  return parts.join(", ");
}

function factsOf(t: Translate, entry: WalkEntry): string {
  const facts: string[] = [];
  if (entry.item.companyName) facts.push(entry.item.companyName);
  if (entry.kind === "sign") {
    const { ownerName, customerNames } = entry.item;
    if (ownerName) facts.push(t("queue_fact_owner", { name: ownerName }));
    if (customerNames.length > 0) facts.push(t("queue_fact_customer", { names: customerNames.join(", ") }));
  }
  facts.push(t("queue_fact_waiting", { days: daysSince(entry.item.since) }));
  return facts.join(" · ");
}

/**
 * The open document with its buttons. Keyed by the document, so the decline form and the
 * reason typed in it start afresh for each one.
 */
function EntryScreen({
  walk,
  view,
  entry,
  isLocked,
}: {
  walk: QueueWalk;
  view: WalkView;
  entry: WalkEntry;
  isLocked: boolean;
}) {
  const [isDeclining, setIsDeclining] = useState(false);
  const { done, declined, skipped, opened } = view.tally;

  return (
    <>
      <DocumentBody entry={entry} mode={view.mode} isLocked={isLocked} hideOnPhone={isDeclining} />
      <SheetFooter
        walk={walk}
        entry={entry}
        phase={view.phase}
        canAct={view.canAct}
        isDeclining={isDeclining}
        onDecliningChange={setIsDeclining}
        justMovedOn={done + declined + skipped + opened > 0}
      />
    </>
  );
}

function DocumentBody({
  entry,
  mode,
  isLocked,
  hideOnPhone,
}: {
  entry: WalkEntry;
  mode: SheetMode;
  isLocked: boolean;
  /** While the decline form is open on a phone, the keyboard leaves room for the form only. */
  hideOnPhone: boolean;
}) {
  const { t } = useTranslation();
  const { item } = entry;
  const leave = entry.kind === "sign" ? entry.item.leave : null;

  return (
    <div className={`flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4 ${hideOnPhone ? "max-md:hidden" : ""}`}>
      <h2 className="text-base font-semibold text-gray-900">{item.title}</h2>
      <p className="text-sm text-gray-600">{factsOf(t, entry)}</p>

      {mode === "team" && leave ? (
        <LeaveSummary leave={leave} />
      ) : (
        // The preview fills its parent, so the parent takes the height the sheet has left; a new key starts it afresh
        // per document. A team request whose leave facts could not be loaded is shown as the document itself.
        <div className="min-h-[20rem] flex-1 overflow-hidden rounded-lg border border-gray-200">
          <PdfView key={item.fileId} id={item.fileId} companyId={item.companyId} />
        </div>
      )}

      {/* Leaving the page while a request runs would hide its answer. */}
      <Link
        to={item.link}
        aria-disabled={isLocked}
        onClick={(event) => isLocked && event.preventDefault()}
        className={`${btnLinkCls} self-start ${isLocked ? "pointer-events-none opacity-50" : ""}`}
      >
        {t("queue_sheet_open_document")}
      </Link>
    </div>
  );
}

/** What a team request is about, in place of a preview. */
function LeaveSummary({ leave }: { leave: LeaveFacts }) {
  const { t } = useTranslation();
  const dates = leave.startDate === leave.endDate ? leave.startDate : `${leave.startDate} – ${leave.endDate}`;

  return (
    // On a phone each label sits above its value: beside it, a name and dates got only 120 px.
    <dl className="grid grid-cols-1 gap-x-3 gap-y-0.5 rounded-lg bg-gray-50 p-4 text-sm sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-y-1.5">
      {leave.employeeName && (
        <>
          <dt className="text-gray-500">{t("queue_leave_employee")}</dt>
          <dd className="mb-2 font-medium text-gray-900 sm:mb-0">{leave.employeeName}</dd>
        </>
      )}
      <dt className="text-gray-500">{t("queue_col_type")}</dt>
      <dd className="mb-2 text-gray-900 sm:mb-0">
        {t(leaveCalendarLeaveTypeI18nKey(leave.leaveType), { defaultValue: leave.leaveType })}
      </dd>
      <dt className="text-gray-500">{t("queue_leave_dates")}</dt>
      <dd className="mb-2 text-gray-900 sm:mb-0">{dates}</dd>
      {leave.startTime && leave.endTime && (
        <>
          <dt className="text-gray-500">{t("queue_leave_hours")}</dt>
          <dd className="text-gray-900">
            {leave.startTime}–{leave.endTime}
          </dd>
        </>
      )}
    </dl>
  );
}
